/**
 * Resilience for transient NETWORK failures (no HTTP response reached us).
 *
 * Why this exists: the cockpit is served from a DuckDNS hostname whose DNS resolution is
 * intermittently flaky — a single page session fires many `/api` calls and any one of them
 * can fail with `net::ERR_NAME_NOT_RESOLVED` (or a momentary connection reset) even though
 * the backend is perfectly healthy. axios surfaces those as an error with NO `response`.
 * Without a retry the chat would give up on the first blip ("Failed to load conversation").
 *
 * We retry ONLY when:
 *   - there is no `error.response` (a true network/DNS failure — the request never reached
 *     the server, so retrying cannot duplicate a side effect), AND
 *   - the request method is idempotent (GET/HEAD) — we never auto-retry a POST/PUT/DELETE,
 *     so a "create conversation" or "send message" can never be double-applied.
 *
 * A real fix for the DNS flakiness is infrastructure-level (pin the host, or move off
 * DuckDNS); this just keeps the UI from face-planting on the inevitable blips.
 */
import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 300;

const IDEMPOTENT = new Set(['get', 'head']);

type RetryConfig = AxiosRequestConfig & { _retryCount?: number };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function attachRetry(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryConfig | undefined;

      // No config, or the server actually answered (4xx/5xx) -> not our job, surface it.
      if (!config || error.response) return Promise.reject(error);

      const method = (config.method || 'get').toLowerCase();
      if (!IDEMPOTENT.has(method)) return Promise.reject(error);

      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount > MAX_RETRIES) return Promise.reject(error);

      // Linear backoff: 300ms, 600ms, 900ms — enough to ride out a DuckDNS resolution miss.
      await sleep(BASE_DELAY_MS * config._retryCount);
      return instance(config);
    }
  );
}
